import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SEOHead } from '@/components/SEOHead';

const sections = [
  { id: 'grundlagen', label: '§ 1 Grundlegende Bestimmungen' },
  { id: 'vertrag', label: '§ 2 Zustandekommen des Vertrages' },
  { id: 'leistung', label: '§ 3 Leistungserbringung' },
  { id: 'domain', label: '§ 4 Domainverwaltung' },
  { id: 'pflichten', label: '§ 5 Weitere Pflichten' },
  { id: 'terrorismus', label: '§ 6 Terroristische Inhalte' },
  { id: 'moderation', label: '§ 7 Moderation von Inhalten' },
  { id: 'laufzeit', label: '§ 8 Vertragslaufzeit, Kündigung' },
  { id: 'zurueckbehalt', label: '§ 9 Zurückbehaltungsrecht' },
  { id: 'rechtswahl', label: '§ 10 Rechtswahl' },
  { id: 'kundeninfo', label: 'II. Kundeninformationen' },
];

const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export default function AGBPage() {
  const [active, setActive] = useState('grundlagen');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    sections.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <Layout>
      <SEOHead
        title="AGB – Allgemeine Geschäftsbedingungen"
        description="Allgemeine Geschäftsbedingungen von DeinServerHost. Stand: 15. Februar 2024."
        canonical="/terms"
        noindex={true}
      />
      {/* Hero */}
      <section className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
          <p className="text-xs font-mono text-primary uppercase tracking-widest mb-3">Rechtliches</p>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-foreground">Allgemeine Geschäftsbedingungen</h1>
          <p className="mt-4 text-muted-foreground">Letzte Aktualisierung: 15. Februar 2024</p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="lg:grid lg:grid-cols-[240px_1fr] lg:gap-12">

          {/* TOC Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-1">
              <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-3 px-3">Inhalt</p>
              {sections.map((s) => (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className={cn(
                    'w-full text-left flex items-center gap-2 px-3 py-1.5 rounded text-sm transition-colors',
                    active === s.id
                      ? 'text-primary bg-primary/10 font-medium'
                      : 'text-muted-foreground hover:text-foreground hover:bg-white/5'
                  )}
                >
                  {active === s.id && <ChevronRight className="w-3 h-3 shrink-0" />}
                  <span className={active === s.id ? '' : 'ml-5'}>{s.label}</span>
                </button>
              ))}
            </div>
          </aside>

          {/* Content */}
          <article className="space-y-10 text-sm text-muted-foreground leading-relaxed" data-testid="agb-content">

            <div className="bg-card border border-border rounded-xl p-5">
              <p className="text-foreground font-semibold text-base mb-2">I. Allgemeine Geschäftsbedingungen</p>
              <p>Die nachstehenden Geschäftsbedingungen gelten für alle Verträge, die Sie mit uns als Anbieter (Christian Ralph Hennig) über die Internetseite deinserverhost.de schließen.</p>
            </div>

            <Section id="grundlagen" title="§ 1 Grundlegende Bestimmungen">
              <Para num="1">Die nachstehenden Geschäftsbedingungen gelten für alle Verträge, die Sie mit uns als Anbieter (Christian Ralph Hennig) über die Internetseite deinserverhost.de schließen. Soweit nicht anders vereinbart, wird der Einbeziehung gegebenenfalls von Ihnen verwendeter eigener Bedingungen widersprochen.</Para>
              <Para num="2">Verbraucher im Sinne der nachstehenden Regelungen ist jede natürliche Person, die ein Rechtsgeschäft zu Zwecken abschließt, die überwiegend weder ihrer gewerblichen noch ihrer selbständigen beruflichen Tätigkeit zugerechnet werden kann. Unternehmer ist jede natürliche oder juristische Person oder eine rechtsfähige Personengesellschaft, die bei Abschluss eines Rechtsgeschäfts in Ausübung ihrer selbständigen beruflichen oder gewerblichen Tätigkeit handelt.</Para>
            </Section>

            <Section id="vertrag" title="§ 2 Zustandekommen des Vertrages">
              <Para num="1">Wir stellen Ihnen Internet-Dienstleistungen, insbesondere Webhosting, Serverhosting und Domains, zur Verfügung. Der Leistungsumfang ergibt sich aus dem von Ihnen gebuchten Leistungspaket und der hierzu auf unserer Internetseite angegebenen Leistungsbeschreibung.</Para>
              <Para num="2">Bereits mit dem Einstellen des jeweiligen Leistungsangebots auf unserer Internetseite unterbreiten wir Ihnen ein verbindliches Angebot zum Abschluss eines Vertrages zu den in der Artikelbeschreibung angegebenen Bedingungen.</Para>
              <Para num="3">Der Vertrag kommt über das Online-Warenkorbsystem zustande. Sie können die zur Buchung beabsichtigte Dienstleistung auswählen, über den Warenkorb bestellen und mit dem Absenden der Bestellung erklären Sie rechtsverbindlich die Annahme des Angebotes.</Para>
              <Para num="4">Ihre Anfragen zur Erstellung eines Angebotes sind für Sie unverbindlich. Wir unterbreiten Ihnen hierzu ein verbindliches Angebot in Textform (z.B. per E-Mail), welches Sie innerhalb von 5 Tagen annehmen können.</Para>
              <Para num="5">Die Abwicklung der Bestellung erfolgt per E-Mail. Sie haben sicherzustellen, dass die von Ihnen hinterlegte E-Mail-Adresse zutreffend ist und der Empfang der E-Mails technisch sichergestellt ist.</Para>
            </Section>

            <Section id="leistung" title="§ 3 Leistungserbringung bei Webhosting und Serverhosting">
              <Para num="1">Die Leistungserbringung (Freischaltung des gebuchten Leistungspakets, Übermittlung der Zugangsdaten) erfolgt, soweit nicht anders vereinbart, innerhalb von 1–2 Tagen nach dem Zeitpunkt Ihrer Zahlungsanweisung.</Para>
              <Para num="2">Soweit wir Ihnen volle Administrationsrechte auf den bereitgestellten Servern einräumen, sind Sie ausschließlich für die Verwaltung und Sicherheit Ihres Servers verantwortlich. Sie sind verpflichtet, notwendige Sicherheitssoftware zu installieren und bekannte Sicherheitslücken selbständig zu schließen.</Para>
              <Para num="3">Soweit wir Programme bereitstellen, erhalten Sie für den Zeitraum der Vertragsdauer ein nicht ausschließliches Recht zur Nutzung. Sie sind verpflichtet, die jeweiligen Lizenzbestimmungen einzuhalten.</Para>
              <Para num="4">Sie sind verpflichtet, Ihren Server so einzurichten, dass die Sicherheit, Integrität und Verfügbarkeit der Netze, anderer Server, Software und Daten Dritter nicht gefährdet wird. Insbesondere ist der Versand von SPAM-Mails und (d)DOS-Attacken untersagt.</Para>
              <Para num="5">Sie haben keinen Anspruch darauf, dass dem Server dieselbe IP-Adresse für die gesamte Vertragslaufzeit zugewiesen wird.</Para>
              <Para num="6">Wir behalten uns vor, die zur Erbringung der Leistungen eingesetzte Hard- und Software an den jeweiligen Stand der Technik anzupassen.</Para>
              <Para num="7">Wir erbringen unsere Leistungen mit einer Verfügbarkeit von 99,2 % im Monatsmittel, soweit im jeweiligen Leistungsangebot keine andere Verfügbarkeit ausgewiesen ist.</Para>
            </Section>

            <Section id="domain" title="§ 4 Domainverwaltung">
              <Para num="1">Bei der Verschaffung von Domains werden wir zwischen Ihnen und den Vergabestellen lediglich als Vermittler tätig. Eine Gewähr für die Übertragung, Zuteilung oder den dauernden Bestand von Domains kann nicht übernommen werden.</Para>
              <Para num="2">Wir sind berechtigt, die Aktivierung einer Domain erst nach Zahlung der vereinbarten Entgelte vorzunehmen.</Para>
              <Para num="3">Erteilen Sie bei Vertragsbeendigung keine klare Weisung zur Übertragung oder Löschung der Domain, können wir die Domain nach Vertragsende und Ablauf einer angemessenen Frist an die zuständige Vergabestelle zurückgeben oder löschen lassen.</Para>
            </Section>

            <Section id="pflichten" title="§ 5 Weitere Pflichten Ihrerseits">
              <Para num="1">Sie haben uns über jede Änderung der für die Vertragserfüllung erforderlichen Daten unverzüglich zu informieren. Passwörter und Zugangsdaten sind streng geheim zu halten.</Para>
              <Para num="2">Sie sind verpflichtet, übermäßige Belastungen unserer Server zu vermeiden. Wir sind berechtigt, Internetseiten oder Server, die diesen Anforderungen nicht gerecht werden, vom Zugriff auszuschließen.</Para>
              <Para num="3">Sie gewährleisten, dass Ihre Domains und die darunter abrufbaren Inhalte nicht gegen gesetzliche Vorschriften oder die guten Sitten verstoßen und keine Rechte Dritter verletzen.</Para>
              <Para num="4">Sie haben Sicherungskopien von allen Daten, die Sie auf unsere Server überspielen, auf gesonderten Datenträgern selbst zu erstellen. Wir sind für die Erstellung von Datensicherungskopien nicht verantwortlich.</Para>
              <Para num="5">Das vereinbarte Datentransfervolumen (Traffic) ist einzuhalten. Bei „unlimited" ist ein Traffic von 10 TB zulässig. Das Betreiben von P2P-Tauschbörsen / Torrents / Filesharing ist untersagt.</Para>
            </Section>

            <Section id="terrorismus" title="§ 6 Verbreitung terroristischer Inhalte">
              <Para num="1">Die Verbreitung terroristischer Inhalte unter Nutzung unserer Dienste ist untersagt.</Para>
              <Para num="2">Terroristische Inhalte im Sinne der Verordnung (EU) 2021/784 sind Materialien, die Anstiftung zur Begehung terroristischer Straftaten, Rekrutierung, Unterweisung in Herstellung von Waffen, oder entsprechende Drohungen beinhalten.</Para>
              <Para num="3">Entfernungsanordnungen der zuständigen Behörde setzen wir spätestens innerhalb einer Stunde nach Erhalt um. Bei sonstigen Meldungen prüfen wir den Inhalt und ergreifen bei Bestätigung unverzüglich Maßnahmen (Ausblenden, Entfernen, Sperrung, Kündigung).</Para>
              <Para num="4">Sie haben die Möglichkeit, gegen von uns ergriffene Maßnahmen innerhalb eines Monats Beschwerde per E-Mail an die im Impressum genannte Kontaktstelle einzulegen.</Para>
            </Section>

            <Section id="moderation" title="§ 7 Moderation von Inhalten">
              <Para num="1">Die Veröffentlichung rechtswidriger Inhalte unter Nutzung unserer Dienste ist untersagt.</Para>
              <Para num="2">Wir behalten uns das Recht vor, Maßnahmen gegen rechtswidrige Inhalte zu ergreifen. Sie sind für rechtswidrige Inhalte, die über Ihre Domain zugänglich gemacht werden, allein verantwortlich.</Para>
              <p className="mt-2">Rechtswidrige Inhalte sind insbesondere solche, die gegen Urheber-, Marken- und Wettbewerbsrecht verstoßen, Straftaten darstellen, gegen Datenschutz verstoßen, oder rassistisch, diskriminierend, beleidigend oder jugendgefährdend sind.</p>
              <Para num="3">Meldungen erfolgen elektronisch an die im Impressum genannte Kontaktstelle. Wir prüfen und ergreifen bei Bestätigung geeignete Maßnahmen.</Para>
              <Para num="4">Sie haben die Möglichkeit, innerhalb eines Monats nach Erhalt unserer Benachrichtigung Beschwerde einzulegen.</Para>
            </Section>

            <Section id="laufzeit" title="§ 8 Vertragslaufzeit, Kündigung">
              <Para num="1">Der Vertrag („Prepaid"-Vertrag) hat eine unbestimmte Laufzeit. Die Zahlung erfolgt jeweils im Voraus für die gewünschte Prepaid-Periode. Erfolgt keine weitere Vorauszahlung bis zum Ablauf der Periode, endet der Vertrag automatisch.</Para>
              <Para num="2">Der Vertrag kann durch uns mit einer Frist von 14 Tagen zum Ende der jeweiligen Prepaid-Periode in Textform gekündigt werden. Gegebenenfalls von Ihnen im Voraus bereits erbrachte Leistungen werden unverzüglich zurückerstattet.</Para>
              <Para num="3">Das Recht zur fristlosen Kündigung aus wichtigem Grund bleibt unberührt. Bei außerordentlicher Kündigung durch uns sind Sie zum Schadenersatz verpflichtet.</Para>
            </Section>

            <Section id="zurueckbehalt" title="§ 9 Zurückbehaltungsrecht">
              <p>Ein Zurückbehaltungsrecht können Sie nur ausüben, soweit es sich um Forderungen aus demselben Vertragsverhältnis handelt.</p>
            </Section>

            <Section id="rechtswahl" title="§ 10 Rechtswahl">
              <Para num="1">Es gilt deutsches Recht. Bei Verbrauchern gilt diese Rechtswahl nur, soweit hierdurch der durch zwingende Bestimmungen des Rechts des Staates des gewöhnlichen Aufenthaltes des Verbrauchers gewährte Schutz nicht entzogen wird.</Para>
              <Para num="2">Die Bestimmungen des UN-Kaufrechts finden ausdrücklich keine Anwendung.</Para>
            </Section>

            <div className="border-t border-border pt-8">
              <Section id="kundeninfo" title="II. Kundeninformationen">
                <div className="space-y-6">
                  <Subsection title="1. Identität des Anbieters">
                    <p>Christian Ralph Hennig<br />Kirchplatz 17<br />66571 Eppelborn<br />Deutschland</p>
                    <p className="mt-2">Telefon: 06806 997 9998<br />E-Mail: info@deinserverhost.de</p>
                  </Subsection>

                  <Subsection title="2. Informationen zum Zustandekommen des Vertrages">
                    <p>Die technischen Schritte zum Vertragsschluss erfolgen nach Maßgabe des § 2 unserer Allgemeinen Geschäftsbedingungen.</p>
                  </Subsection>

                  <Subsection title="3. Vertragssprache, Vertragstextspeicherung">
                    <p>3.1. Vertragssprache ist deutsch.</p>
                    <p className="mt-1">3.2. Der vollständige Vertragstext wird von uns nicht gespeichert. Vor Absenden der Bestellung können die Vertragsdaten über die Druckfunktion des Browsers gesichert werden. Nach Zugang der Bestellung werden die Bestelldaten und die AGB nochmals per E-Mail übersandt.</p>
                  </Subsection>

                  <Subsection title="4. Wesentliche Merkmale der Dienstleistung">
                    <p>Die wesentlichen Merkmale der Dienstleistung finden sich im jeweiligen Angebot.</p>
                  </Subsection>

                  <Subsection title="5. Preise und Zahlungsmodalitäten">
                    <p>5.1. Die in den Angeboten angeführten Preise stellen Gesamtpreise dar und beinhalten alle anfallenden Steuern.</p>
                    <p className="mt-2 font-medium text-foreground">Zahlungsmöglichkeiten:</p>
                    <ul className="mt-1 space-y-0.5 list-disc list-inside">
                      <li>PayPal</li>
                      <li>PaysafeCard</li>
                      <li>Sofort</li>
                      <li>Skrill</li>
                      <li>Bit-pay (Bitcoins)</li>
                      <li>Vorkasse per Überweisung</li>
                    </ul>
                    <p className="mt-2">5.3. Zahlungsansprüche sind, sofern nicht anders angegeben, sofort zur Zahlung fällig.</p>
                  </Subsection>

                  <Subsection title="6. Gesetzliches Mängelhaftungsrecht">
                    <p>Es bestehen die gesetzlichen Mängelhaftungsrechte.</p>
                  </Subsection>

                  <Subsection title="7. Vertragslaufzeit, Kündigung">
                    <p>Informationen zur Laufzeit und Kündigung finden Sie in § 8 der AGB sowie im jeweiligen Angebot.</p>
                  </Subsection>
                </div>
              </Section>
            </div>

            <div className="bg-card border border-border rounded-xl p-5 text-xs text-muted-foreground">
              Diese AGB und Kundeninformationen wurden von den auf IT-Recht spezialisierten Juristen des Händlerbundes erstellt und werden permanent auf Rechtskonformität geprüft.{' '}
              <a href="https://www.haendlerbund.de/agb-service" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                www.haendlerbund.de/agb-service
              </a>
              <br />Letzte Aktualisierung: 15.02.2024
            </div>

            {/* Footer Nav */}
            <div className="pt-4 border-t border-border flex flex-wrap gap-4 text-sm text-muted-foreground">
              <Link to="/imprint" className="hover:text-foreground transition-colors">Impressum</Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors">Datenschutzerklärung</Link>
              <Link to="/cookie-settings" className="hover:text-foreground transition-colors">Cookie-Einstellungen</Link>
            </div>
          </article>
        </div>
      </div>
    </Layout>
  );
}

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24">
    <h2 className="text-lg font-bold text-foreground mb-4 pb-2 border-b border-border">{title}</h2>
    <div className="space-y-3">{children}</div>
  </section>
);

const Para = ({ num, children }) => (
  <div className="flex gap-3">
    <span className="shrink-0 font-semibold text-primary">({num})</span>
    <p>{children}</p>
  </div>
);

const Subsection = ({ title, children }) => (
  <div>
    <h3 className="font-semibold text-foreground mb-2">{title}</h3>
    <div className="text-muted-foreground space-y-1">{children}</div>
  </div>
);
